<template>
  <b-modal
    :active="active"
    :can-cancel="isUploading ? [] : ['escape', 'outside']"
    has-modal-card
    @close="$emit('cancel')"
  >
    <div class="modal-card preview-card">
      <header class="modal-card-head">
        <p class="modal-card-title has-text-centered">{{ title }}</p>
      </header>

      <section class="modal-card-body">
        <p class="preview-description">{{ description }}</p>

        <div class="preview-list">
          <div class="preview-item" v-for="device in devices" :key="device.key">
            <div class="preview-frame" :class="`preview-frame--${device.key}`">
              <div class="preview-screen" :style="device.screenStyle">
                <div class="totem-app" v-if="device.kind === 'totem'">
                  <div
                    class="totem-app-bar totem-app-header"
                    :style="device.headerStyle"
                  >
                    <span class="totem-dot" :style="accentStyle"></span>
                    <span class="totem-title-group">
                      <span class="totem-bar totem-bar--title"></span>
                      <span class="totem-bar totem-bar--subtitle"></span>
                    </span>
                    <span class="totem-dot" :style="accentStyle"></span>
                  </div>

                  <div class="totem-app-body">
                    <span class="totem-scan-frame">
                      <span class="totem-scan-line"></span>
                    </span>
                  </div>

                  <div
                    class="totem-app-bar totem-app-footer"
                    :style="device.footerStyle"
                  >
                    <span class="totem-dot" :style="accentStyle"></span>
                    <span class="totem-bar totem-bar--total"></span>
                    <span class="totem-pill" :style="accentStyle"></span>
                  </div>
                </div>

                <div class="phone-app" v-else>
                  <div class="phone-header" :style="device.headerStyle">
                    <span class="phone-chevron" :style="accentTextStyle"
                      >&lsaquo;</span
                    >
                    <span class="phone-bar phone-bar--checkout"></span>
                  </div>

                  <div class="phone-body">
                    <span class="phone-basket">
                      <span
                        class="phone-basket-handle"
                        :style="accentStyle"
                      ></span>
                      <span
                        class="phone-basket-body"
                        :style="accentStyle"
                      ></span>
                    </span>
                    <span class="phone-bar phone-bar--empty-title"></span>
                    <span class="phone-bar phone-bar--empty-text"></span>
                  </div>

                  <div class="phone-footer" :style="device.footerStyle">
                    <div class="phone-footer-row">
                      <span class="phone-bar phone-bar--total-label"></span>
                      <span
                        class="phone-bar phone-bar--total-value"
                        :style="accentStyle"
                      ></span>
                    </div>
                    <div class="phone-footer-row">
                      <span class="phone-catalog" :style="accentStyle"></span>
                      <span
                        class="phone-scan-button"
                        :style="accentStyle"
                      ></span>
                    </div>
                  </div>
                </div>

                <div class="preview-overlay">
                  <img
                    v-if="imageUrl"
                    :src="imageUrl"
                    class="preview-promo-image"
                    :class="`preview-promo-image--${device.kind}`"
                    :style="device.promoStyle"
                    alt=""
                  />
                  <span class="preview-close" :style="device.closeStyle">
                    <span
                      class="preview-close-glyph"
                      :style="device.closeGlyphStyle"
                      >&times;</span
                    >
                  </span>
                </div>
              </div>
            </div>

            <div class="totem-stand" v-if="device.kind === 'totem'"></div>
            <p class="preview-caption">{{ device.label }}</p>
          </div>
        </div>
      </section>

      <footer
        class="modal-card-foot"
        :class="{ 'preview-foot--upload': !isViewMode }"
      >
        <b-button :disabled="isUploading" @click="$emit('cancel')">
          {{ isViewMode ? $t("common.close") : $t("common.cancel") }}
        </b-button>
        <b-button
          v-if="!isViewMode"
          type="is-primary"
          class="preview-confirm"
          :loading="isUploading"
          :disabled="isUploading || !imageUrl"
          @click="$emit('confirm')"
        >
          {{ $t("promotionalImage.confirmUpload") }}
        </b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
// Cada modelo descreve a tela real do dispositivo em pixels, para que o mockup
// mantenha as mesmas proporções em que a imagem promocional é exibida.
const DEVICE_MODELS = [
  {
    key: "horizontal",
    kind: "totem",
    labelKey: "promotionalImage.horizontalTotem",
    screenWidth: 1024,
    screenHeight: 647,
    headerRatio: 0.178,
    footerRatio: 0.232,
    closeRatio: 0.043,
    closeTop: 0.035,
    closeRight: 0.025,
    closeBackground: "#133b23"
  },
  {
    key: "vertical",
    kind: "totem",
    labelKey: "promotionalImage.verticalTotem",
    screenWidth: 702,
    screenHeight: 1024,
    headerRatio: 0.132,
    footerRatio: 0.186,
    closeRatio: 0.08,
    closeTop: 0.035,
    closeRight: 0.025,
    closeBackground: "#133b23"
  },
  {
    // App do cliente (onii-customer): o pop-up da tela do carrinho renderiza a
    // imagem em uma caixa fixa de 300x400 esticada (sem object-fit), com o
    // botão de fechar do Buefy a 20px do topo/direita da tela.
    key: "smartphone",
    kind: "phone",
    labelKey: "promotionalImage.customerApp",
    screenWidth: 390,
    screenHeight: 844,
    headerRatio: 0.066,
    footerRatio: 0.173,
    promoWidth: 300,
    promoHeight: 400,
    closeRatio: 0.082,
    closeTop: 0.024,
    closeRight: 0.051,
    closeBackground: "#999999"
  }
];

export default {
  name: "PromotionalImagePreviewModal",

  props: {
    active: {
      type: Boolean,
      default: false
    },
    imageUrl: {
      type: String,
      default: ""
    },
    isUploading: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: "upload",
      validator: value => ["upload", "view"].indexOf(value) >= 0
    }
  },

  computed: {
    isViewMode() {
      return this.mode === "view";
    },

    title() {
      return this.isViewMode
        ? this.$t("promotionalImage.promotionalImageTitle")
        : this.$t("promotionalImage.previewTitle");
    },

    description() {
      return this.isViewMode
        ? this.$t("promotionalImage.currentImageDescription")
        : this.$t("promotionalImage.previewDescription");
    },

    accentStyle() {
      return { backgroundColor: this.$primaryColor };
    },

    accentTextStyle() {
      return { color: this.$primaryColor };
    },

    devices() {
      return DEVICE_MODELS.map(model => ({
        key: model.key,
        kind: model.kind,
        label: this.$t(model.labelKey),
        screenStyle: {
          paddingTop: `${(model.screenHeight / model.screenWidth) * 100}%`
        },
        headerStyle: { height: `${model.headerRatio * 100}%` },
        footerStyle: { height: `${model.footerRatio * 100}%` },
        // No app a imagem é esticada para caber na caixa fixa; nos totens ela
        // ocupa a tela toda respeitando o aspecto original.
        promoStyle: model.promoWidth
          ? {
              width: `${(model.promoWidth / model.screenWidth) * 100}%`,
              height: `${(model.promoHeight / model.screenHeight) * 100}%`
            }
          : {},
        closeStyle: {
          width: `${model.closeRatio * 100}em`,
          height: `${model.closeRatio * 100}em`,
          top: `${model.closeTop * 100}%`,
          right: `${model.closeRight * 100}%`,
          backgroundColor: model.closeBackground
        },
        closeGlyphStyle: { fontSize: `${model.closeRatio * 60}em` }
      }));
    }
  }
};
</script>

<style scoped>
.preview-card {
  max-width: 960px;
  width: 100%;
}

.preview-foot--upload {
  justify-content: center;
}

.preview-description {
  margin-bottom: 24px;
  text-align: center;
}

.preview-list {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: center;
}

.preview-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 16px 8px 16px;
}

.preview-frame {
  background-color: #1c1c1e;
  border-radius: 12px;
  padding: 9px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
}

/* 1em = 1% da largura da tela (largura do frame menos as bordas), para o
   mockup escalar junto com o frame */
.preview-frame--horizontal {
  width: 366px;
  font-size: 3.48px;
}

.preview-frame--vertical {
  width: 232px;
  font-size: 2.14px;
}

.preview-frame--smartphone {
  width: 170px;
  font-size: 1.58px;
  border-radius: 20px;
  padding: 6px;
}

.preview-screen {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: #e9e9e9;
  border-radius: 5px;
}

.preview-frame--smartphone .preview-screen {
  border-radius: 14px;
}

.preview-overlay {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
}

.preview-frame--smartphone .preview-overlay {
  background-color: rgba(10, 10, 10, 0.86);
}

.preview-promo-image {
  border-radius: 6px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}

.preview-promo-image--totem {
  max-width: 91%;
  max-height: 92%;
  object-fit: contain;
}

/* O app usa uma caixa fixa de 300x400 sem object-fit: a imagem é esticada. */
.preview-promo-image--phone {
  object-fit: fill;
  border-radius: 0;
}

.preview-close {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.preview-close-glyph {
  color: #ffffff;
  line-height: 1;
}

.preview-caption {
  margin-top: 10px;
  font-weight: 600;
}

.preview-confirm {
  margin-left: 10px;
}

/* ---------- mockup dos totens ---------- */

.totem-app {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
}

.totem-app-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0 4em;
  background-color: #133b23;
}

.totem-app-header {
  border-bottom-left-radius: 5em;
  border-bottom-right-radius: 5em;
}

.totem-app-footer {
  border-top-left-radius: 5em;
  border-top-right-radius: 5em;
}

.totem-app-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.totem-dot {
  width: 9em;
  height: 9em;
  border-radius: 50%;
  flex-shrink: 0;
}

.totem-title-group {
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-left: 3em;
  margin-right: 3em;
}

.totem-bar {
  display: block;
  border-radius: 2em;
  background-color: rgba(255, 255, 255, 0.75);
}

.totem-bar--title {
  width: 42%;
  height: 3.4em;
}

.totem-bar--subtitle {
  width: 26%;
  height: 2.2em;
  margin-top: 2em;
  background-color: rgba(255, 255, 255, 0.4);
}

.totem-bar--total {
  width: 20%;
  height: 3.4em;
}

.totem-pill {
  width: 26em;
  height: 8em;
  border-radius: 2.5em;
  flex-shrink: 0;
}

.totem-scan-frame {
  position: relative;
  display: block;
  width: 56%;
  height: 34%;
  border: 1em solid rgba(0, 0, 0, 0.28);
  border-radius: 2em;
}

.totem-scan-line {
  position: absolute;
  top: 50%;
  left: 6%;
  width: 88%;
  height: 0.8em;
  background-color: #d0342c;
}

.totem-stand {
  width: 60px;
  height: 10px;
  background-color: #1c1c1e;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* ---------- mockup do app do cliente ---------- */

.phone-app {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  background-color: #f7f7f7;
}

.phone-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0 5em;
  background-color: #ffffff;
}

.phone-chevron {
  font-size: 14em;
  font-weight: bold;
  line-height: 1;
}

.phone-bar {
  display: block;
  border-radius: 2em;
  background-color: #d5d5d5;
}

.phone-bar--checkout {
  width: 24%;
  height: 4em;
  background-color: #b5b5b5;
}

.phone-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Silhueta do carrinho vazio exibido pelo app quando não há itens */
.phone-basket {
  position: relative;
  width: 26%;
  height: 13%;
  opacity: 0.22;
}

.phone-basket-body {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  height: 62%;
  clip-path: polygon(0 0, 100% 0, 86% 100%, 14% 100%);
}

.phone-basket-handle {
  position: absolute;
  top: 18%;
  left: 20%;
  width: 60%;
  height: 2.5em;
  border-radius: 2em;
  transform: rotate(-22deg);
}

.phone-bar--empty-title {
  width: 44%;
  height: 4em;
  margin-top: 6em;
  background-color: #c2c2c2;
}

.phone-bar--empty-text {
  width: 62%;
  height: 2.6em;
  margin-top: 3em;
  background-color: #dcdcdc;
}

.phone-footer {
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 5em;
  background-color: #ffffff;
  border-top: 0.6em solid #ededed;
}

.phone-footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 2.5em 0;
}

.phone-bar--total-label {
  width: 16%;
  height: 3em;
}

.phone-bar--total-value {
  width: 32%;
  height: 6em;
  border-radius: 1.5em;
}

.phone-catalog {
  width: 14%;
  height: 13em;
  border-radius: 2em;
  opacity: 0.2;
  flex-shrink: 0;
}

.phone-scan-button {
  width: 78%;
  height: 13em;
  border-radius: 2em;
  flex-shrink: 0;
}
</style>
